'use client';

import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { css } from '@bts-web/utils-style-engine';
import { Locale } from '@bts-web/utils-lokalise';
import { PrecisionFloat } from '@bts-web/utils-formatting';
import { MutableNonNullableAsset, TradeInnerTranslatedValues } from '../types';
import {
  TradeControllerVariants,
  TradeOperation,
  TradeScreenTranslations,
} from '../../utils';
import { TradeBuyApplicationStateReacter } from '../../Reusable/ApplicationState/TradeBuyApplicationStateReacter.use-client/TradeBuyApplicationStateReacter.use-client';
import { TradeAssetInfoDisplay } from '../../../common/components/TradeAssetInfoDisplay/TradeAssetInfoDisplay';
import { MainTradeForm } from '../MainTradeForm';
import {
  getMaximumTradeAmount,
  getMinimumTradeAmount,
  getTradeQuickSelectButtons,
} from '../utils';
import { useTradeDataRefresher } from './hooks/useTradeDataRefresher';
import { useAppNotification } from '../../../notifications/NotificationContextProvider';
import { useModalDrawerContext } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { TARGET_PRICE_KEY, VERIFY_LINK_KEY } from '../constants';
import { clearLegacyPendingTradeFlowKeyAction } from '../legacy-freeze/legacyPendingTradeFlowCookie.actions';
import { OrderVerificationLinkForm } from '../MainTradeForm/subcomponents/OrderVerificationLinkForm';
import { useIsBelowScreenWidth } from '../../../layouts/utils/useIsBelowScreenWidth';
import { getOrdersCount } from '../../../common/gqlActions';
import { StopOrderInfoDrawer } from '../../../orders/components/StopOrderInfoDrawer/StopOrderInfoDrawer';
import { OrderOperation } from '../../../orders/types';
import { useClientUserInfo } from '../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { getAppConfig } from '@bts-web/core-app-config';

export type MainTradeDataFlowWrapperProps = {
  staticData: {
    currentLocale: Locale;
    translations: TradeScreenTranslations;
    variant: TradeControllerVariants;
    currency: string;
    translatedInnerValues: TradeInnerTranslatedValues;
    orderOperation?: OrderOperation;
  };
  initialData: {
    assetData: MutableNonNullableAsset;
    targetAssetData?: MutableNonNullableAsset;
    initialSwapExchangeRate?: PrecisionFloat;
    stakedFiat?: PrecisionFloat;
    stakedAsset?: PrecisionFloat;
    rewardFiat?: PrecisionFloat;
    rewardAsset?: PrecisionFloat;
  };
  overrideOperationWithVariant?: TradeControllerVariants;
  openBuyTab?: () => void;
};

const mainTradeWrapperStyles = css({
  px: 'medium',
  display: 'flex',
  flexDirection: 'column',
  flex: 'auto',
  minHeight: '100vh',
  lg: {
    minHeight: 'unset',
  },
});

export const MainTradeDataFlowWrapper: FC<MainTradeDataFlowWrapperProps> = ({
  initialData,
  staticData,
  overrideOperationWithVariant,
  openBuyTab,
}) => {
  const { setAppNotification, closeNotificationById, closeAllNotifications } =
    useAppNotification();

  const { openDrawer, close } = useModalDrawerContext();

  const hasCheckedOrders = useRef(false);

  const searchParams = useSearchParams();

  const params = useParams();

  const userData = useClientUserInfo();

  const { minimumSavingsAmount } = getAppConfig();

  const isUserTradingActive = userData?.tradingActive as boolean;

  const hasOrderTargetPrice =
    searchParams.has(TARGET_PRICE_KEY) &&
    staticData.variant === TradeOperation.order;

  const offerIsCreated = Boolean(params?.offerId);

  const verifyLink = searchParams.get(VERIFY_LINK_KEY);

  const {
    currentLocale,
    translations,
    variant,
    currency,
    translatedInnerValues,
    orderOperation,
  } = staticData;

  const {
    isDataRefreshing,
    assetCurrentData,
    targetAssetData,
    swapExchangeRate,
    isTradingActive,
    price,
  } = useTradeDataRefresher({
    initialAssetData: initialData.assetData,
    transactionType: variant,
    initialExchangeRate: initialData.initialSwapExchangeRate,
    initialTargetAssetData: initialData.targetAssetData,
    orderOperation: staticData.orderOperation,
  });

  const { __typename: assetType, symbol, id: assetId } = assetCurrentData ?? {};

  const { assetBalance, fiatBalance } = assetCurrentData.portfolio ?? {};

  const { minimumBuyAmount, minimumSellAmount, staking } = assetCurrentData;

  const isMobile = useIsBelowScreenWidth(1024);

  const showOrderInfoDrawer = useCallback(() => {
    if (
      variant !== TradeOperation.order ||
      hasOrderTargetPrice ||
      offerIsCreated
    )
      return;

    let isAborted = false;

    if (hasCheckedOrders.current || isMobile === null) return;

    const checkOrders = async () => {
      hasCheckedOrders.current = true;

      const { count } = await getOrdersCount({
        size: 1,
      });

      if (isAborted) return;

      if (count === 0) {
        openDrawer(StopOrderInfoDrawer, {
          translations,
          title: translations.stopOrder,
          position: isMobile ? 'bottom' : 'right',
        });
      }
    };

    checkOrders();

    return () => {
      isAborted = true;

      close();
    };
  }, [variant, isMobile]);

  // Legacy-only: clear pending order flow cookie when trade flow starts. Remove with legacy flow. See trade/AccepBackwardsCompatibilityInterceptor/legacy-freeze/README.md
  useEffect(() => {
    if (!verifyLink) {
      clearLegacyPendingTradeFlowKeyAction();
    }
  }, [verifyLink]);

  useEffect(() => {
    if (!isUserTradingActive) {
      setAppNotification({
        title: translations.errorTradingNotPossibleTitle,
        subtitle: translations.errorTradingNotPossibleSubtitle,
        visual: 'info',
        customDuration: 'infinite',
        position: 'bottomRight',
        id: 'trading-not-active',
      });

      return;
    }

    return () => {
      closeAllNotifications();
    };
  }, []);

  useEffect(() => {
    [initialData.assetData, initialData.targetAssetData].forEach((asset) => {
      if (asset) {
        closeNotificationById(`asset-not-active-${asset.id}`);
      }

      if (!asset) {
        return;
      }

      const assetisInactive = asset && asset?.isActive === false;

      const isBuyInactiveOnBuyAsset =
        asset?.id === initialData.assetData.id &&
        !initialData.assetData.isBuyActive &&
        (variant === TradeOperation.buy ||
          (variant === TradeOperation.order &&
            orderOperation === OrderOperation.BUY));

      const isSellInactiveOnSellAsset =
        asset?.id === initialData.assetData.id &&
        !initialData.assetData.isSellActive &&
        (variant === TradeOperation.sell ||
          (variant === TradeOperation.order &&
            orderOperation === OrderOperation.SELL));

      const isBuyInactiveOnSavingsPlansBuyAsset =
        asset?.id === initialData.assetData.id &&
        !initialData.assetData.isBuyActive &&
        variant === TradeOperation.savings;

      const isSellInactiveOnSwappedAsset =
        asset?.id === initialData.assetData.id &&
        !initialData.assetData.isSellActive &&
        variant === TradeOperation.swap;

      const isBuyInactiveOnSwappedTargetAsset =
        asset?.id === initialData.targetAssetData?.id &&
        !initialData.targetAssetData?.isBuyActive &&
        variant === TradeOperation.swap;

      const isStakeInactiveOnAsset =
        asset?.id === initialData.assetData.id &&
        !initialData.assetData.staking?.enabled &&
        (variant === TradeOperation.stake ||
          variant === TradeOperation.unstake);

      if (
        assetisInactive ||
        isBuyInactiveOnBuyAsset ||
        isBuyInactiveOnSwappedTargetAsset ||
        isSellInactiveOnSwappedAsset ||
        isSellInactiveOnSellAsset ||
        isBuyInactiveOnSavingsPlansBuyAsset ||
        isStakeInactiveOnAsset
      ) {
        setAppNotification({
          title: `${translations.errorAssetNotAvailableTitle}`,
          subtitle: translations.errorAssetNotAvailableSubtitle,
          visual: 'info',
          customDuration: 5000,
          position: 'bottomRight',
          id: `asset-not-active-${asset.id}`,
        });
      }
    });
  }, [initialData.assetData, initialData.targetAssetData, variant]);

  // Show stop order info drawer if there are no existing stop orders for the user
  useEffect(showOrderInfoDrawer, [showOrderInfoDrawer]);

  useEffect(() => {
    // Show asset orders disabled info if isEnabledForOrders is false
    if (variant === TradeOperation.order) {
      if (!assetCurrentData.isEnabledForOrders) {
        setAppNotification({
          title: `${translations.errorAssetNotAvailableTitle}`,
          subtitle: translations.errorAssetNotAvailableSubtitle,
          visual: 'info',
          position: 'bottomRight',
          customDuration: 'infinite',
          id: `asset-orders-not-active-${assetCurrentData.id}`,
          withClose: true,
        });
      }
    }

    return () => {
      closeNotificationById(`asset-orders-not-active-${assetCurrentData.id}`);
    };
  }, [assetCurrentData, variant]);

  const { maximumTradeAmount, minimumTradeAmount, quickSelectTradeItems } =
    useMemo(() => {
      let currentAssetBalance: PrecisionFloat = assetBalance;

      let currentFiatBalance: PrecisionFloat = fiatBalance;

      if (variant === TradeOperation.stake) {
        if (
          initialData.assetData.portfolio?.edges?.length &&
          initialData.assetData.portfolio?.edges[0]?.node?.assets?.edges
        ) {
          currentAssetBalance = {
            value: String(
              Number(assetBalance?.value || '0') -
                Number(
                  initialData.assetData.portfolio?.edges[0]?.node?.assets
                    ?.edges[0]?.stakedBalance?.value || '0',
                ),
            ),
            precision: assetBalance?.precision,
          };

          // Replace with BE value. fiatTotalStakedAmount contains also reward: BU-3858
          currentFiatBalance = {
            value: String(
              Number(fiatBalance?.value || '0') -
                Number(
                  initialData.assetData.portfolio?.edges[0]?.node?.assets
                    ?.edges[0]?.fiatTotalStakedAmount?.value || '0',
                ),
            ),
            precision: fiatBalance?.precision,
          };
        }
      }

      if (variant === TradeOperation.unstake) {
        if (
          initialData.assetData.portfolio?.edges?.length &&
          initialData.assetData.portfolio?.edges[0]?.node?.assets?.edges
        ) {
          currentAssetBalance = {
            value: String(
              Number(initialData.stakedAsset?.value || '0') +
                Number(initialData.rewardAsset?.value || '0'),
            ),
            precision: assetBalance?.precision,
          };

          // Replace with BE value. fiatTotalStakedAmount contains also reward: BU-3858
          currentFiatBalance = {
            value: String(
              Number(initialData.stakedFiat?.value || '0') +
                Number(initialData.rewardFiat?.value || '0'),
            ),
            precision: fiatBalance?.precision,
          };
        }
      }

      const maximumTradeAmount = getMaximumTradeAmount({
        assetBalance: currentAssetBalance,
        fiatBalance: currentFiatBalance,
        type: variant,
        orderOperation,
      });

      const minimumTradeAmount = getMinimumTradeAmount({
        minimumBuyAmount,
        minimumSellAmount,
        minimumStakeableAmount: staking?.minimumStakeableAmount,
        minimumSavingsAmount,
        price,
        type: variant,
        orderOperation,
      });

      const quickSelectTradeItems = getTradeQuickSelectButtons({
        currentLocale,
        maximumTradeAmount,
        price,
        transactionType: variant,
        translatedInnerValues: translatedInnerValues,
        orderOperation,
        searchParams,
      });

      return {
        maximumTradeAmount,
        minimumTradeAmount,
        quickSelectTradeItems,
      };
    }, [
      translatedInnerValues,
      variant,
      assetBalance,
      fiatBalance,
      minimumBuyAmount,
      minimumSellAmount,
      minimumSavingsAmount,
      staking,
      price,
      currentLocale,
      orderOperation,
    ]);

  const finalSymbol =
    assetCurrentData?.__typename === 'MetalAsset' ? translations.grams : symbol;

  const userCannotTrade =
    !userData.tradingActive || userData.hasTermsNotAccepted === true;

  if (variant === TradeOperation.order && verifyLink) {
    return (
      <div className={mainTradeWrapperStyles}>
        <OrderVerificationLinkForm
          isDataLoading={false}
          translations={translations}
          verifyLink={verifyLink}
        />
      </div>
    );
  }

  return (
    <div className={mainTradeWrapperStyles}>
      <TradeBuyApplicationStateReacter translations={translations} />

      <TradeAssetInfoDisplay
        isDataLoading={isDataRefreshing}
        sourceAssetData={assetCurrentData}
        symbol={finalSymbol as string}
        {...(variant === TradeOperation.swap
          ? { swapExchangeRate, targetAssetData }
          : {})}
        price={price}
        operation={variant}
      />

      <MainTradeForm
        locale={currentLocale}
        isDataLoading={isDataRefreshing}
        assetDetails={{
          name: assetCurrentData.name,
          logoUrl: assetCurrentData.logoUrl,
          id: assetId,
          assetType: assetType,
          symbol: finalSymbol,
          price: price,
          assetIsNotTradeable:
            !isTradingActive ||
            (variant === TradeOperation.order &&
              !assetCurrentData.isEnabledForOrders),
          portfolio: assetCurrentData.portfolio,
          __typename: assetCurrentData.__typename,
        }}
        targetAssetData={targetAssetData}
        currentTradeDetails={{
          maximumTradeAmount: maximumTradeAmount,
          minimumTradeAmount: minimumTradeAmount,
          quickSelectTradeItems: quickSelectTradeItems,
          userCannotTrade: userCannotTrade,
          swapExchangeRate: swapExchangeRate,
        }}
        staticValues={{
          currentLocale: currentLocale,
          currency: currency,
          translations: translations,
          translatedValues: translatedInnerValues,
          transactionType: variant,
          orderOperation: orderOperation,
        }}
        overrideOperationWithVariant={overrideOperationWithVariant}
        openBuyTab={openBuyTab}
      />
    </div>
  );
};
